import {
  Disclosure,
  Field,
  Input,
  Pill,
  Row,
  Rows,
  Section,
  Segmented,
  Select,
} from "@/components/kit";
import type { FixtureTypeDef, MasterDefinition, OutcomeModel } from "@/lib/api/types";
import { toCode } from "@/lib/builder";

import { Fields, Names } from "./Fields";

/**
 * The kinds of contest a sport holds.
 *
 * Swimming's individual race and its relay. Tennis's singles and doubles.
 * Cycling's road stage and its team time trial — where the *entrant* changes
 * from a rider to a team, which is the line between a kind and a config: a
 * config changes the rules, a kind changes the shape of the contest.
 */

const OUTCOMES: { value: OutcomeModel; label: string }[] = [
  { value: "DUEL", label: "Two sides meet" },
  { value: "FIELD", label: "A field, ranked" },
  { value: "SERIES", label: "A set of attempts" },
  { value: "COMPOSITE", label: "Made of smaller fixtures" },
];

/** What each outcome actually means, in the operator's words. */
const OUTCOME_NOTE: Record<OutcomeModel, string> = {
  DUEL: "Two sides, and someone wins. A match, a bout, a rubber.",
  FIELD: "Many start together and finish in an order. A race, a heat.",
  SERIES: "Each entrant takes a set of attempts. A high jump, a lift.",
  COMPOSITE: "A result folded from smaller fixtures. A tie, a decathlon.",
};

export function KindsSection({
  bare = false,
  kinds,
  masters,
  onChange,
}: {
  kinds: FixtureTypeDef[];
  masters: MasterDefinition[];
  onChange: (kinds: FixtureTypeDef[]) => void;
  /** Render the contents without the section's own heading and rule.
   *  The wizard shows one section per step and supplies the heading itself, so
   *  the section's would be a second copy of it on the same screen. */
  bare?: boolean;
}) {
  const parties = masters.filter((m) => m.binding.kind === "participant");
  const asOptions = masters.map((m) => ({ code: m.code, label: m.label || m.code }));

  const replace = (i: number, patch: Partial<FixtureTypeDef>) =>
    onChange(kinds.map((k, j) => (j === i ? { ...k, ...patch } : k)));

  const add = () =>
    onChange([
      ...kinds,
      {
        code: "",
        label: "",
        outcome: "DUEL",
        entrant: { master: parties[0]?.code ?? "", min: 2, max: 2 },
      },
    ]);

  const contents = (
    <>
      <Rows
        empty="No kinds yet. A sport needs at least one before anything can be entered."
        count={kinds.length}
        addLabel="Add a kind of fixture"
        onAdd={add}
        onMove={(from, to) => {
          const next = [...kinds];
          const [moved] = next.splice(from, 1);
          next.splice(to, 0, moved);
          onChange(next);
        }}
        onRemove={(i) => onChange(kinds.filter((_, j) => j !== i))}
      >
        {kinds.map((kind, i) => (
          <Row key={i} index={i} badge={<Pill>{kind.code || "no name yet"}</Pill>}>
            <Field label="Called" required htmlFor={`k-${i}-label`}>
              <Input
                id={`k-${i}-label`}
                value={kind.label}
                placeholder="Match"
                onChange={(e) =>
                  replace(i, {
                    label: e.target.value,
                    code: kind.code === toCode(kind.label) ? toCode(e.target.value) : kind.code,
                  })
                }
              />
            </Field>

            <Field label="Who enters" htmlFor={`k-${i}-entrant`}>
              <Select
                id={`k-${i}-entrant`}
                value={kind.entrant.master}
                onChange={(e) =>
                  replace(i, { entrant: { ...kind.entrant, master: e.target.value } })
                }
              >
                <option value="">Pick one</option>
                {parties.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.label || m.code}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Least" htmlFor={`k-${i}-min`}>
              <Input
                id={`k-${i}-min`}
                type="number"
                value={kind.entrant.min ?? ""}
                onChange={(e) =>
                  replace(i, {
                    entrant: {
                      ...kind.entrant,
                      min: e.target.value === "" ? null : Number(e.target.value),
                    },
                  })
                }
              />
            </Field>

            <Field label="Most" hint="Blank for no limit" htmlFor={`k-${i}-max`}>
              <Input
                id={`k-${i}-max`}
                type="number"
                value={kind.entrant.max ?? ""}
                onChange={(e) =>
                  replace(i, {
                    entrant: {
                      ...kind.entrant,
                      max: e.target.value === "" ? null : Number(e.target.value),
                    },
                  })
                }
              />
            </Field>

            {/* Labelled. Four unlabelled tabs gave no clue what they were choosing
                between — the note below says what the chosen one means, but only
                after you have guessed. */}
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="How it is decided" hint={OUTCOME_NOTE[kind.outcome]}>
                <Segmented
                  name="How it is decided"
                  value={kind.outcome}
                  options={OUTCOMES}
                  onChange={(outcome) => replace(i, { outcome })}
                />
              </Field>
            </div>

            {kind.outcome === "DUEL" && (
              <div style={{ gridColumn: "1 / -1" }}>
                <Names
                  id={`k-${i}-alignments`}
                  label="Side names"
                  hint="Name every side or none — Home, Away"
                  placeholder="Home, Away"
                  values={kind.alignments ?? []}
                  onChange={(alignments) => replace(i, { alignments })}
                />
              </div>
            )}

            {/* Three field sets, and the difference between them is *when* each is
                known. Titles say that plainly; the old ones ("Known before — the
                start list") assumed you already knew. Shut when empty, so a card
                is three lines rather than three boxes.

                What each entrant *ends up with* is deliberately not here. Results
                arrive from the scorer, which is a different layer with its own
                screens, so declaring their shape is not part of onboarding a
                sport. `resultFields` still exists on the model and the entry form
                still renders whatever a definition already carries — this is the
                authoring surface dropping a job that was never its own. */}
            <div style={{ gridColumn: "1 / -1" }}>
              <Disclosure
                title="What you know when it is scheduled"
                note="Per entrant, before anything happens: the lane, the stall, the bib number, the seeding. This is what the start list asks for."
                count={(kind.entryFields ?? []).length}
              >
                <Fields
                  what="entry detail"
                  fields={kind.entryFields ?? []}
                  masters={asOptions}
                  onChange={(entryFields) => replace(i, { entryFields })}
                />
              </Disclosure>
              <Disclosure
                title="The rules of the contest"
                note="Overs, rounds, distance, a time limit. Not typed per fixture — a config on the next step answers these once, and every fixture played that way carries the same answers."
                count={(kind.format ?? []).length}
              >
                <Fields
                  what="rule"
                  fields={kind.format ?? []}
                  masters={asOptions}
                  onChange={(format) => replace(i, { format })}
                />
              </Disclosure>
              <Disclosure
                title="One fixture's own facts"
                note="The toss, the attendance, the going, the belts it is for. Typed by whoever enters that fixture, and true of that fixture only."
                count={(kind.meta ?? []).length}
              >
                <Fields
                  what="fact"
                  fields={kind.meta ?? []}
                  masters={asOptions}
                  onChange={(meta) => replace(i, { meta })}
                />
              </Disclosure>
            </div>
          </Row>
        ))}
      </Rows>
    </>
  );

  if (bare) return contents;

  return (
    <Section
      title="Kinds of fixture"
      note="A kind changes the shape of the contest — who enters, how it is decided. Different rules for the same shape are a config, below."
    >
      {contents}
    </Section>
  );
}
